import { faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Button from 'components/base/Button';
import { useCallback, useEffect, useState } from 'react';
import { Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import {
  exportFoodData,
  ExportColumnKey,
  getExportColumnOptions
} from 'services/exportService';
import { convertToExcel } from 'utils/excelExportUtils';
import moment from 'moment';

interface ExportClinic {
  id: number;
  name: string;
}

interface ExportDietitian {
  id: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  role?: string;
}

interface ExportFoodDataModalProps {
  show: boolean;
  onClose: () => void;
  userRole?: string;
  userClinicalExportEnabled?: boolean;
  userResearchExportEnabled?: boolean;
  userClinicId?: number;
  fixedPatientId?: number;
  clinicId?: number;
  dietitianId?: number;
  patientId?: number;
}

const ExportFoodDataModal = ({
  show,
  onClose,
  userRole = 'clinic',
  userClinicalExportEnabled = false,
  userResearchExportEnabled = false,
  userClinicId,
  fixedPatientId,
  clinicId: initialClinicId,
  dietitianId: initialDietitianId,
  patientId: initialPatientId
}: ExportFoodDataModalProps) => {
  const [exportType, setExportType] = useState<'clinical' | 'research'>(
    'clinical'
  );
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<
    ExportColumnKey[]
  >(() => getExportColumnOptions('clinical').map(column => column.key));

  // For patient exports, use wide date range to capture all data
  // For admin/clinic exports, default to last 30 days
  const isPatientExport = !!fixedPatientId;
  const [dateFrom, setDateFrom] = useState(
    isPatientExport
      ? moment('2000-01-01').format('YYYY-MM-DD') // Capture all data from 2000
      : moment().subtract(30, 'days').format('YYYY-MM-DD')
  );
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>(() => {
    if (initialClinicId) return [Number(initialClinicId)];
    if (userClinicId) return [Number(userClinicId)];
    return [];
  });
  const [selectedPatientId, setSelectedPatientId] = useState<
    number | undefined
  >(() => {
    if (initialPatientId) return Number(initialPatientId);
    if (fixedPatientId) return Number(fixedPatientId);
    return undefined;
  });
  const [clinics, setClinics] = useState<ExportClinic[]>([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false);
  const [dietitians, setDietitians] = useState<ExportDietitian[]>([]);
  const [dietitiansLoading, setDietitiansLoading] = useState(false);
  const [dietitianDropdownOpen, setDietitianDropdownOpen] = useState(false);
  const [selectedDietitianIds, setSelectedDietitianIds] = useState<number[]>(
    () => (initialDietitianId ? [Number(initialDietitianId)] : [])
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = userRole === 'superadmin';
  const isClinic = userRole === 'clinic';

  useEffect(() => {
    if (!show || !isSuperAdmin) return;

    const loadClinics = async () => {
      try {
        setClinicsLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/clinic/sa`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data?.status === false) {
          setError(response.data?.error?.message || 'Failed to load clinics');
          return;
        }

        setClinics(response.data?.data?.clinics || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load clinics');
      } finally {
        setClinicsLoading(false);
      }
    };

    loadClinics();
  }, [show, isSuperAdmin]);

  useEffect(() => {
    if (!show || !isClinic) return;

    const loadDietitians = async () => {
      try {
        setDietitiansLoading(true);
        const token = localStorage.getItem('token');
        const urls = [`${process.env.REACT_APP_ENDPOINT}/clinic/dietitians`];

        const responses = await Promise.all(
          urls.map(url =>
            axios.get(url, {
              headers: { Authorization: `Bearer ${token}` }
            })
          )
        );

        const users = responses.flatMap(response => {
          const data = response.data?.data || {};
          return data.users || data.dietitians || [];
        });

        setDietitians(
          Array.from(
            new Map(
              users
                .filter(
                  (user: ExportDietitian) =>
                    String(user.role || '').toUpperCase() === 'DIETITIAN'
                )
                .map((user: ExportDietitian) => [Number(user.id), user])
            ).values()
          )
        );
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load dietitians'
        );
      } finally {
        setDietitiansLoading(false);
      }
    };

    loadDietitians();
  }, [show, isClinic]);

  const handleExport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const payload: any = {
        exportType,
        dateFrom,
        dateTo,
        columns: selectedColumnKeys
      };

      const patientId = fixedPatientId || selectedPatientId;
      if (patientId) {
        payload.patientId = Number(patientId);
      }

      // Validate and set clinicId
      if (isClinic) {
        // For clinic users, clinicId is required and comes from userClinicId
        const clinicIdValue = Number(userClinicId);
        if (!clinicIdValue || isNaN(clinicIdValue)) {
          setError('Your clinic information is missing. Please log in again.');
          setLoading(false);
          return;
        }
        payload.clinicId = clinicIdValue;
      } else if (isSuperAdmin && selectedClinicIds.length) {
        // For superadmin, clinicId is optional - omit it for All Clinics.
        payload.clinicId = selectedClinicIds;
      }

      if (isClinic && selectedDietitianIds.length) {
        payload.dietitianId = selectedDietitianIds;
      }

      const response = await exportFoodData(payload);

      if (!response.status) {
        setError(response.data?.message || 'Export failed');
        setLoading(false);
        return;
      }

      // Convert to Excel and download
      await convertToExcel(
        response.data.foodLogs,
        response.data.patientSummaries,
        exportType,
        dateFrom,
        dateTo,
        selectedColumnKeys
      );

      // Close modal on success
      onClose();
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to export data';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [
    exportType,
    dateFrom,
    dateTo,
    selectedColumnKeys,
    fixedPatientId,
    selectedPatientId,
    selectedClinicIds,
    selectedDietitianIds,
    isSuperAdmin,
    isClinic,
    userClinicId,
    onClose
  ]);

  const canExportClinical = userClinicalExportEnabled;
  const canExportResearch = userResearchExportEnabled;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      contentClassName="border"
    >
      <Modal.Header className="ps-card border-bottom border-translucent">
        <Modal.Title className="modal-title text-body-highlight mb-0">
          Export Food Data
        </Modal.Title>
        <Button className="p-1" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-8" />
        </Button>
      </Modal.Header>

      <Modal.Body className="px-card pb-card pt-card fs-9">
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="mb-3">
          <Form.Label className="fw-bold">Export Type *</Form.Label>
          <div>
            {canExportClinical && (
              <Form.Check
                type="radio"
                id="export-clinical"
                label="Clinical (includes patient name & hospital file ID)"
                name="exportType"
                value="clinical"
                checked={exportType === 'clinical'}
                onChange={() => {
                  setExportType('clinical');
                  setSelectedColumnKeys(
                    getExportColumnOptions('clinical').map(column => column.key)
                  );
                }}
                disabled={loading}
              />
            )}
            {canExportResearch && (
              <Form.Check
                type="radio"
                id="export-research"
                label="Research (excludes patient identification)"
                name="exportType"
                value="research"
                checked={exportType === 'research'}
                onChange={() => {
                  setExportType('research');
                  setSelectedColumnKeys(
                    getExportColumnOptions('research').map(column => column.key)
                  );
                }}
                disabled={loading}
              />
            )}
            {!canExportClinical && !canExportResearch && (
              <div className="text-muted">
                No export types enabled for this user
              </div>
            )}
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Which columns to include?</Form.Label>
          <div className="row">
            {getExportColumnOptions(exportType).map(column => (
              <div className="col-md-6" key={column.key}>
                <Form.Check
                  type="checkbox"
                  id={`export-column-${column.key}`}
                  label={column.label}
                  checked={selectedColumnKeys.includes(column.key)}
                  onChange={() =>
                    setSelectedColumnKeys(currentKeys =>
                      currentKeys.includes(column.key)
                        ? currentKeys.filter(key => key !== column.key)
                        : [...currentKeys, column.key]
                    )
                  }
                  disabled={loading}
                />
              </div>
            ))}
          </div>
        </Form.Group>

        {!isPatientExport && (
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Date From *</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Date To *</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
            </div>
          </div>
        )}

        {isPatientExport && (
          <div className="alert alert-info mb-3">
            <small>
              Exporting all available food data for this patient (from 2000 to
              present)
            </small>
          </div>
        )}

        {isSuperAdmin && (
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-bold mb-0">
                Hospital/Site (Optional)
              </Form.Label>
            </div>
            <Dropdown
              show={clinicDropdownOpen}
              onToggle={setClinicDropdownOpen}
              className="w-100"
            >
              <Dropdown.Toggle
                variant="light"
                className="w-100 text-start border d-flex justify-content-between align-items-center"
                disabled={loading || clinicsLoading}
              >
                <span>
                  {selectedClinicIds.length
                    ? `${selectedClinicIds.length} clinic${
                        selectedClinicIds.length === 1 ? '' : 's'
                      } selected`
                    : 'All Clinics'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-2">
                <div
                  style={{ maxHeight: 190, overflowY: 'auto' }}
                  onClick={event => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`dropdown-item rounded d-flex align-items-center mb-1 ${
                      !selectedClinicIds.length ? 'active' : ''
                    }`}
                    onClick={() => {
                      setSelectedClinicIds([]);
                      setClinicDropdownOpen(false);
                    }}
                    disabled={loading || clinicsLoading}
                  >
                    <strong>All Clinics</strong>
                  </button>

                  {clinicsLoading ? (
                    <div className="text-body-secondary px-2 py-2">
                      Loading clinics...
                    </div>
                  ) : clinics.length ? (
                    clinics.map(clinic => (
                      <button
                        type="button"
                        key={clinic.id}
                        className={`dropdown-item rounded d-flex align-items-center ${
                          selectedClinicIds.includes(Number(clinic.id))
                            ? 'active'
                            : ''
                        }`}
                        onClick={() => {
                          const clinicId = Number(clinic.id);
                          setSelectedClinicIds(currentIds =>
                            currentIds.includes(clinicId)
                              ? currentIds.filter(id => id !== clinicId)
                              : [...currentIds, clinicId]
                          );
                          setClinicDropdownOpen(false);
                        }}
                        disabled={loading}
                      >
                        {clinic.name}
                      </button>
                    ))
                  ) : (
                    <div className="text-body-secondary px-2 py-2">
                      No clinics available.
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {selectedClinicIds.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {selectedClinicIds.map(clinicId => {
                  const clinic = clinics.find(
                    item => Number(item.id) === clinicId
                  );
                  return (
                    <span
                      key={clinicId}
                      className="badge bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-2 py-2 px-3"
                    >
                      {clinic?.name || `Clinic ${clinicId}`}
                      <button
                        type="button"
                        className="btn-close btn-close-sm"
                        aria-label={`Remove ${clinic?.name || 'clinic'}`}
                        onClick={() =>
                          setSelectedClinicIds(currentIds =>
                            currentIds.filter(id => id !== clinicId)
                          )
                        }
                        disabled={loading}
                      />
                    </span>
                  );
                })}
              </div>
            )}
            <Form.Text className="text-muted">
              Select one or more clinics, or choose All Clinics to export all
              clinic data.
            </Form.Text>
          </Form.Group>
        )}

        {isClinic && (
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Dietitian (Optional)</Form.Label>
            <Dropdown
              show={dietitianDropdownOpen}
              onToggle={setDietitianDropdownOpen}
              className="w-100"
            >
              <Dropdown.Toggle
                variant="light"
                className="w-100 text-start border d-flex justify-content-between align-items-center"
                disabled={loading || dietitiansLoading}
              >
                <span>
                  {selectedDietitianIds.length
                    ? `${selectedDietitianIds.length} dietitian${
                        selectedDietitianIds.length === 1 ? '' : 's'
                      } selected`
                    : 'All Dietitians'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-2">
                <div
                  style={{ maxHeight: 190, overflowY: 'auto' }}
                  onClick={event => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`dropdown-item rounded mb-1 ${
                      !selectedDietitianIds.length ? 'active' : ''
                    }`}
                    onClick={() => {
                      setSelectedDietitianIds([]);
                      setDietitianDropdownOpen(false);
                    }}
                    disabled={loading || dietitiansLoading}
                  >
                    <strong>All Dietitians</strong>
                  </button>

                  {dietitiansLoading ? (
                    <div className="text-body-secondary px-2 py-2">
                      Loading dietitians...
                    </div>
                  ) : dietitians.length ? (
                    dietitians.map(dietitian => (
                      <button
                        type="button"
                        key={dietitian.id}
                        className={`dropdown-item rounded ${
                          selectedDietitianIds.includes(Number(dietitian.id))
                            ? 'active'
                            : ''
                        }`}
                        onClick={() => {
                          const id = Number(dietitian.id);
                          setSelectedDietitianIds(currentIds =>
                            currentIds.includes(id)
                              ? currentIds.filter(itemId => itemId !== id)
                              : [...currentIds, id]
                          );
                          setDietitianDropdownOpen(false);
                        }}
                        disabled={loading}
                      >
                        {`${dietitian.firstname || ''} ${
                          dietitian.lastname || ''
                        }`.trim() ||
                          dietitian.email ||
                          `User ${dietitian.id}`}
                      </button>
                    ))
                  ) : (
                    <div className="text-body-secondary px-2 py-2">
                      No dietitians available.
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {selectedDietitianIds.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {selectedDietitianIds.map(dietitianId => {
                  const dietitian = dietitians.find(
                    item => Number(item.id) === dietitianId
                  );
                  const name =
                    `${dietitian?.firstname || ''} ${
                      dietitian?.lastname || ''
                    }`.trim() ||
                    dietitian?.email ||
                    `User ${dietitianId}`;

                  return (
                    <span
                      key={dietitianId}
                      className="badge bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-2 py-2 px-3"
                    >
                      {name}
                      <button
                        type="button"
                        className="btn-close btn-close-sm"
                        aria-label={`Remove ${name}`}
                        onClick={() =>
                          setSelectedDietitianIds(currentIds =>
                            currentIds.filter(id => id !== dietitianId)
                          )
                        }
                        disabled={loading}
                      />
                    </span>
                  );
                })}
              </div>
            )}
            <Form.Text className="text-muted">
              Select one or more dietitians, or leave All Dietitians selected.
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer className="px-card pb-card border-top border-translucent d-flex justify-content-end gap-2">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
          title="Cancel export"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={loading || (!canExportClinical && !canExportResearch)}
          startIcon={
            loading ? undefined : <FontAwesomeIcon icon={faDownload} />
          }
          title={
            loading
              ? 'Preparing export...'
              : canExportClinical || canExportResearch
              ? 'Download export as Excel'
              : 'No export permissions'
          }
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Preparing...
            </>
          ) : (
            'Download Export'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportFoodDataModal;
